import { useCallback, useEffect, useRef, useState } from "react";
import { Bot, Copy, Download, Loader2, PlayCircle, RefreshCw, CheckCircle2 } from "lucide-react";
import { api, errText } from "../../lib/api";

const Toggle = ({ on, onClick, label, hint, testid, busy }) => (
  <button onClick={onClick} disabled={busy} data-testid={testid}
          className="flex items-start gap-3 text-left w-full group">
    <span className={`mt-0.5 w-9 h-5 rounded-full shrink-0 transition-colors relative ${on ? "bg-success" : "bg-line"}`}>
      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${on ? "left-[18px]" : "left-0.5"}`} />
    </span>
    <span>
      <span className="text-sm text-ink block">{label}</span>
      <span className="text-xs text-muted">{hint}</span>
    </span>
  </button>
);

const Secret = ({ label, value, onRotate, testid, hint }) => {
  const [show, setShow] = useState(false);
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard?.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  return (
    <div className="space-y-1" data-testid={testid}>
      <p className="smallcaps">{label}</p>
      {hint ? <p className="text-xs text-muted">{hint}</p> : null}
      <div className="flex flex-wrap items-center gap-2">
        <code className="text-[11px] bg-ink/5 rounded px-2 py-1.5 flex-1 min-w-[220px] break-all">
          {show ? value : value.slice(0, 10) + "••••••••••••" + value.slice(-4)}
        </code>
        <button className="btn-ghost text-[11px] px-2" onClick={() => setShow(!show)} data-testid={`${testid}-show`}>
          {show ? "Hide" : "Show"}
        </button>
        <button className="btn-ghost text-[11px] px-2 inline-flex items-center gap-1" onClick={copy} data-testid={`${testid}-copy`}>
          <Copy size={11} /> {copied ? "Copied" : "Copy"}
        </button>
        {onRotate ? (
          <button className="btn-ghost text-[11px] px-2 inline-flex items-center gap-1 text-danger" onClick={onRotate}
                  data-testid={`${testid}-rotate`}>
            <RefreshCw size={11} /> Rotate
          </button>
        ) : null}
      </div>
    </div>
  );
};

const CHECKLIST = [
  ["Request access", "In the Stripe Dashboard go to Agentic commerce and join the Agentic Commerce Suite seller waitlist (US preview). Stripe must approve and onboard your endpoint before agents send traffic."],
  ["Business profile", "Complete your Stripe business profile with the legal entity, and make sure your Terms, Privacy and Refund policy URLs are filled in (links below are sent on every session)."],
  ["Register your endpoint", "During onboarding give Stripe your checkout base URL (shown below) plus the Bearer token and Signing secret from this page. Confirm the signature convention Stripe shows you; then switch on \u201cRequire signature\u201d here."],
  ["Upload the product feed", "Download the EUR feed CSV below (USD variant also available) and upload it in Dashboard → Agentic commerce → Product catalog, choosing REPLACE mode. Re-upload whenever bouquets change — deactivated or deleted bouquets are also emitted as delete rows for upsert mode."],
  ["Enable agents", "Request onboarding for the agents you want (e.g. ChatGPT Instant Checkout) and accept the agent terms. Set capture mode to Immediate."],
  ["Test, then go live", "Use Workbench → Blueprints to fire sandbox create / update / complete requests at the endpoint. When Stripe confirms, save your LIVE Stripe key in Settings → Payments and switch Test mode OFF here."],
];

export default function AgenticCommercePage() {
  const [d, setD] = useState(null);
  const [preview, setPreview] = useState(null);
  const [sim, setSim] = useState(null);
  const nextTraceKey = useRef(0);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/acp"); setD(data); }
    catch (e) { setErr(errText(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    setBusy("save"); setErr("");
    try { const { data } = await api.put("/admin/acp", patch); setD(data); }
    catch (e) { setErr(errText(e)); }
    finally { setBusy(""); }
  };

  const rotate = async (which) => {
    if (!window.confirm("Rotate this credential? The old value stops working immediately — you must update it on the Stripe side too.")) return;
    setBusy("rotate"); setErr("");
    try { const { data } = await api.post("/admin/acp/rotate", { which }); setD(data); setMsg("Credential rotated."); }
    catch (e) { setErr(errText(e)); }
    finally { setBusy(""); }
  };

  const loadPreview = async (currency) => {
    setBusy("preview"); setErr("");
    try { const { data } = await api.get(`/admin/acp/feed-preview?currency=${currency}`); setPreview(data); }
    catch (e) { setErr(errText(e)); }
    finally { setBusy(""); }
  };

  const simulate = async () => {
    setBusy("simulate"); setErr(""); setSim(null);
    try { const { data } = await api.post("/admin/acp/simulate"); setSim({ ...data, transcript: (data.transcript || []).map(t => ({ ...t, uiKey: `trace-${nextTraceKey.current++}` })) }); await load(); }
    catch (e) { setErr(errText(e)); }
    finally { setBusy(""); }
  };

  if (!d) return <p className="text-sm text-muted" data-testid="acp-loading">Loading…</p>;

  return (
    <div className="space-y-5" data-testid="admin-acp-page">
      <div>
        <h1 className="font-display text-3xl text-ink inline-flex items-center gap-2"><Bot size={26} /> Agentic commerce</h1>
        <p className="text-sm text-muted max-w-2xl">
          Sell through AI shopping agents (ChatGPT Instant Checkout and others) via Stripe's Agentic
          Commerce Protocol. Agents discover your bouquets through the product feed and check buyers out
          against this store directly — every agent order lands in your normal pipeline with partner
          acceptance, e-mails and refunds.
        </p>
      </div>

      {msg ? <p className="text-sm text-success inline-flex items-center gap-1.5" data-testid="acp-message"><CheckCircle2 size={14} /> {msg}</p> : null}
      {err ? <p className="text-sm text-danger" data-testid="acp-error">{err}</p> : null}

      <div className="card p-4 space-y-4" data-testid="acp-status-panel">
        <p className="smallcaps">Status</p>
        <div className="grid sm:grid-cols-3 gap-4">
          <Toggle on={d.enabled} busy={busy === "save"} testid="acp-enable-toggle"
                  onClick={() => save({ enabled: !d.enabled })}
                  label={d.enabled ? "Enabled" : "Disabled"}
                  hint="Agents can call the checkout endpoints" />
          <Toggle on={d.test_mode} busy={busy === "save"} testid="acp-test-toggle"
                  onClick={() => save({ test_mode: !d.test_mode })}
                  label={d.test_mode ? "Test mode" : "Live payments"}
                  hint="Test mode simulates the card charge; switch off once Stripe onboards your account" />
          <Toggle on={d.require_signature} busy={busy === "save"} testid="acp-signature-toggle"
                  onClick={() => save({ require_signature: !d.require_signature })}
                  label="Require signature"
                  hint="Reject unsigned requests — turn on after confirming the scheme with Stripe" />
        </div>
      </div>

      <div className="card p-4 space-y-4" data-testid="acp-credentials-panel">
        <p className="smallcaps">Endpoint & credentials — give these to Stripe at onboarding</p>
        <div className="space-y-1">
          <p className="smallcaps">Checkout endpoint base URL</p>
          <code className="text-[11px] bg-ink/5 rounded px-2 py-1.5 inline-block break-all" data-testid="acp-endpoint-url">{d.endpoint_base}</code>
          <p className="text-xs text-muted">Stripe calls {"{base}"}/checkout_sessions, /{"{id}"}, /{"{id}"}/complete and /{"{id}"}/cancel.</p>
        </div>
        <Secret label="Bearer token" value={d.bearer_token} testid="acp-bearer"
                hint="Sent by Stripe in the Authorization header on every request."
                onRotate={() => rotate("bearer")} />
        <Secret label="Signing secret" value={d.signing_secret} testid="acp-signing"
                hint="HMAC-SHA256 over “{timestamp}.{body}”, base64, in the Signature header."
                onRotate={() => rotate("signing")} />
      </div>

      <div className="card p-4 space-y-3" data-testid="acp-feed-panel">
        <p className="smallcaps">Product feed</p>
        <p className="text-xs text-muted">
          {d.stats.bouquets} published bouquets + {d.stats.extras} extras. Only live products are included —
          anything deactivated, drafted or deleted is dropped and also emitted as an explicit delete row.
          Upload the CSV in Stripe → Agentic commerce → Product catalog (replace mode) and re-upload after
          catalogue changes.
        </p>
        {Object.entries(d.feed_urls).map(([cur, url]) => (
          <div key={cur} className="flex flex-wrap items-center gap-2" data-testid={`acp-feed-url-${cur.toLowerCase()}`}>
            <span className="text-xs font-medium text-ink w-10">{cur}</span>
            <code className="text-[11px] bg-ink/5 rounded px-2 py-1.5 flex-1 min-w-[240px] break-all">{url}</code>
            <a className="btn-ghost text-[11px] px-2 inline-flex items-center gap-1" href={url} target="_blank" rel="noreferrer"
               data-testid={`acp-feed-download-${cur.toLowerCase()}`}>
              <Download size={11} /> Download
            </a>
            <button className="btn-ghost text-[11px] px-2" onClick={() => loadPreview(cur)} data-testid={`acp-feed-preview-${cur.toLowerCase()}`}>
              Preview
            </button>
          </div>
        ))}
        <div className="flex items-center gap-3">
          <button className="btn-ghost text-[11px] px-2 inline-flex items-center gap-1 text-danger"
                  onClick={() => rotate("feed")} data-testid="acp-feed-rotate">
            <RefreshCw size={11} /> Rotate feed URL key
          </button>
          {d.feed_state?.EUR?.generated_at ? (
            <span className="text-xs text-muted">Last EUR feed: {d.feed_state.EUR.row_count} rows · {d.feed_state.EUR.generated_at.slice(0, 16).replace("T", " ")}</span>
          ) : null}
        </div>
        {preview ? (
          <div className="overflow-x-auto" data-testid="acp-feed-preview-table">
            <p className="text-xs text-muted mb-1">{preview.total_rows} live rows in the {preview.currency} feed — first {preview.rows.length}:</p>
            <table className="text-[11px] w-full">
              <thead><tr className="text-left text-muted">
                <th className="pr-3 py-1">id</th><th className="pr-3">title</th><th className="pr-3">price</th><th className="pr-3">availability</th>
              </tr></thead>
              <tbody>
                {preview.rows.map((r) => (
                  <tr key={r.id} className="border-t border-line/60">
                    <td className="pr-3 py-1 max-w-[220px] truncate">{r.id}</td>
                    <td className="pr-3 max-w-[260px] truncate">{r.title}</td>
                    <td className="pr-3">{(r.price / 100).toFixed(2)} {r.currency}</td>
                    <td className="pr-3">{r.availability}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>

      <div className="card p-4 space-y-3" data-testid="acp-simulate-panel">
        <p className="smallcaps">End-to-end simulator</p>
        <p className="text-xs text-muted">
          Runs a full agent checkout against your real engine — create session → pick a delivery slot →
          complete with a test payment token. It creates a <strong>real test order</strong> in your pipeline
          (partner alert + buyer e-mail) so you can verify the whole chain, then cancel it from Admin → Orders.
        </p>
        <button className="btn-primary text-[11px] px-4 inline-flex items-center gap-2" onClick={simulate}
                disabled={busy === "simulate"} data-testid="acp-simulate-btn">
          {busy === "simulate" ? <Loader2 size={13} className="animate-spin" /> : <PlayCircle size={13} />}
          Run simulated agent checkout
        </button>
        {sim ? (
          <div className="space-y-2" data-testid="acp-simulate-result">
            {sim.ok ? <p className="text-sm text-success inline-flex items-center gap-1.5"><CheckCircle2 size={14} /> Order {sim.order_number} created through the agent flow.</p>
              : <p className="text-sm text-danger">{sim.error}</p>}
            <div className="text-[11px] bg-ink/5 rounded p-2 space-y-1">
              {sim.transcript.map((t) => (
                <p key={t.uiKey} data-testid={`acp-${t.uiKey}`}><span className="font-medium">{t.step}:</span> {Object.entries(t).filter(([k]) => k !== "step" && k !== "uiKey").map(([k, v]) => `${k}=${Array.isArray(v) ? v.join(" | ") : v}`).join(" · ")}</p>
              ))}
            </div>
          </div>
        ) : null}
      </div>

      <div className="card p-4 space-y-2" data-testid="acp-sessions-panel">
        <p className="smallcaps">Agent checkout sessions</p>
        {d.sessions.length === 0 ? <p className="text-xs text-muted" data-testid="acp-sessions-empty">No agent sessions yet.</p> : (
          <div className="overflow-x-auto">
            <table className="text-[11px] w-full" data-testid="acp-sessions-table">
              <thead><tr className="text-left text-muted">
                <th className="pr-3 py-1">Session</th><th className="pr-3">Agent</th><th className="pr-3">Buyer</th>
                <th className="pr-3">Total</th><th className="pr-3">Status</th><th className="pr-3">Order</th><th>Created</th>
              </tr></thead>
              <tbody>
                {d.sessions.map((s) => (
                  <tr key={s.id} className="border-t border-line/60" data-testid={`acp-session-${s.id}`}>
                    <td className="pr-3 py-1 max-w-[150px] truncate">{s.id}</td>
                    <td className="pr-3">{s.agent}</td>
                    <td className="pr-3 max-w-[160px] truncate">{s.buyer_email}</td>
                    <td className="pr-3">€{s.total_eur.toFixed(2)}</td>
                    <td className="pr-3">{s.status}{s.test_mode ? " · test" : ""}</td>
                    <td className="pr-3">{s.order_number || "—"}</td>
                    <td>{(s.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card p-4 space-y-3" data-testid="acp-checklist-panel">
        <p className="smallcaps">What to set up on the Stripe side</p>
        <ol className="space-y-2">
          {CHECKLIST.map(([title, body], i) => (
            <li key={title} className="flex gap-3">
              <span className="w-5 h-5 rounded-full bg-ink text-white text-[10px] grid place-items-center shrink-0 mt-0.5">{i + 1}</span>
              <span className="text-xs"><span className="font-medium text-ink">{title}.</span> <span className="text-muted">{body}</span></span>
            </li>
          ))}
        </ol>
        <p className="text-xs text-muted">
          Note: agent orders are always charged immediately in EUR through Stripe (TRYB is never used for
          agent traffic) and then follow the normal partner acceptance, e-mail and refund flows.
        </p>
      </div>
    </div>
  );
}
